import { Link } from "react-router-dom";
import { useTranslations } from "@/lib/useTranslations";
import type { ReactNode } from "react";

import { EventsLibraryExportButton } from "@/components/ui/admin/events/EventsLibraryExportButton";
import { EventsLibraryFiltersPanel } from "@/components/ui/admin/events/EventsLibraryFiltersPanel";
import { EventsLibrarySearchForm } from "@/components/ui/admin/events/EventsLibrarySearchForm";
import { EventsLibraryQuickAddRow } from "@/components/ui/admin/events/EventsLibraryQuickAddRow";
import { LibraryNavigableRowLink } from "@/components/ui/admin/LibraryNavigableRowLink";
import { LibraryResultsFreezeControl } from "@/components/ui/admin/LibraryResultsFreezeControl";
import { LibrarySortableHeader } from "@/components/ui/admin/LibrarySortableHeader";
import { LibraryChecklistProgress } from "@/components/ui/admin/LibraryChecklistProgress";
import { LibraryPagination } from "@/components/ui/admin/LibraryPagination";
import { LibraryAssigneeCell } from "@/components/ui/admin/LibraryAssigneeCell";
import { defaultLocale } from "@/i18n/config";
import { formatEventCalendarDate } from "@/lib/event-dates";
import type { ContentStatus } from "@/core/services/types/article.types";
import type {
  EventAdminListItem,
  EventLibraryCategoryFilter,
  EventLibraryCategoryCounts,
  EventLibraryDateFilters,
  EventLibraryTab,
  EventLibraryTabCounts,
  EventLibrarySortState,
} from "@/core/services/types/event.types";
import {
  EVENT_LIBRARY_TABS,
  buildEventLibraryHref,
  buildEventLibrarySortHref,
  getEventEditorialChecklistProgress,
  getEventSharePath,
} from "@/core/services/types/event.types";
import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";
import type { LibrarySortDirection } from "@/core/services/types/library-sort.types";

interface EventsLibraryPanelProps {
  items: EventAdminListItem[];
  total: number;
  page: number;
  pageSize: LibraryPageSize;
  tab: EventLibraryTab;
  category: EventLibraryCategoryFilter;
  dateFilters: EventLibraryDateFilters;
  tabCounts: EventLibraryTabCounts;
  categoryCounts: EventLibraryCategoryCounts;
  searchQuery: string;
  sort: EventLibrarySortState;
  isRefreshing?: boolean;
  isFrozen?: boolean;
  onToggleResultsFreeze?: () => void;
  canFreezeResults?: boolean;
}

const libraryDateCellClassName =
  "border-r border-black/5 px-4 py-3 min-w-0 text-neutral-600 leading-snug";

function LibraryTableDateCell({ children }: { children: ReactNode }) {
  return (
    <td className={libraryDateCellClassName}>
      <span className="block whitespace-normal break-words">{children}</span>
    </td>
  );
}

function formatDate(value: string | null): string {
  return formatEventCalendarDate(value, defaultLocale);
}

function formatUpdatedAt(value: string): string {
  return new Intl.DateTimeFormat(defaultLocale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatCreatedAt(value: string): string {
  return formatUpdatedAt(value);
}

function formatPublishedAt(value: string | null): string {
  if (!value) return "—";

  return new Intl.DateTimeFormat(defaultLocale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function buildLibraryHref(
  tab: EventLibraryTab,
  targetPage: number,
  searchQuery: string,
  category: EventLibraryCategoryFilter,
  dateFilters: EventLibraryDateFilters,
  sort: EventLibrarySortState,
  currentPageSize: LibraryPageSize,
): string {
  return buildEventLibraryHref({
    tab,
    page: targetPage,
    pageSize: currentPageSize,
    searchQuery,
    category,
    dateFilters,
    sort,
  });
}

function getSortDirection(sort: EventLibrarySortState): LibrarySortDirection {
  return sort?.direction ?? "desc";
}

function buildSortHref(
  tab: EventLibraryTab,
  searchQuery: string,
  category: EventLibraryCategoryFilter,
  dateFilters: EventLibraryDateFilters,
  sort: EventLibrarySortState,
  pageSize: LibraryPageSize,
  nextColumn: Parameters<typeof buildEventLibrarySortHref>[0]["nextColumn"],
): string {
  return buildEventLibrarySortHref({
    tab,
    searchQuery,
    category,
    dateFilters,
    sort,
    pageSize,
    nextColumn,
  });
}

function CategoryLabel({ event }: { event: EventAdminListItem }) {
  const t = useTranslations("events.library.categoryFilters");

  if (!event.event_category) {
    return <span className="text-neutral-400">—</span>;
  }

  return t(event.event_category);
}

function PlaceCell({ event }: { event: EventAdminListItem }) {
  if (!event.place?.name) {
    return <span className="text-neutral-400">—</span>;
  }

  return (
    <span className="block min-w-0">
      <span className="block truncate font-medium text-whart-text">
        {event.place.name}
      </span>
      {event.place.city ? (
        <span className="block truncate text-xs text-neutral-500">
          {event.place.city}
        </span>
      ) : null}
    </span>
  );
}

function StatusBadge({ event }: { event: EventAdminListItem }) {
  const t = useTranslations("events.library");

  const styles: Record<ContentStatus, string> = {
    SIGNAL: "bg-violet-50 text-violet-700",
    DRAFT: "bg-neutral-100 text-neutral-600",
    PENDING_REVIEW: "bg-amber-50 text-amber-700",
    SCHEDULED: "bg-amber-50 text-amber-800",
    PUBLISHED: "bg-emerald-50 text-emerald-700",
    ARCHIVED: "bg-neutral-100 text-neutral-500",
  };

  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${styles[event.status]}`}
    >
      {t(`statuses.${event.status}`)}
    </span>
  );
}

export function EventsLibraryPanel({
  items,
  total,
  page,
  pageSize,
  tab,
  category,
  dateFilters,
  tabCounts,
  categoryCounts,
  searchQuery,
  sort,
  isRefreshing = false,
  isFrozen = false,
  onToggleResultsFreeze,
  canFreezeResults = false,
}: EventsLibraryPanelProps) {
  const t = useTranslations("events.library");
  const tCommon = useTranslations("common");

  const buildPaginationHref = (targetPage: number, targetPageSize = pageSize) =>
    buildLibraryHref(
      tab,
      targetPage,
      searchQuery,
      category,
      dateFilters,
      sort,
      targetPageSize,
    );

  return (
    <section className="flex w-full min-w-0 flex-col gap-4">
      <EventsLibrarySearchForm
        tab={tab}
        category={category}
        dateFilters={dateFilters}
        searchQuery={searchQuery}
        sort={sort}
        pageSize={pageSize}
      />

      <EventsLibraryFiltersPanel
        tab={tab}
        category={category}
        categoryCounts={categoryCounts}
        dateFilters={dateFilters}
        searchQuery={searchQuery}
        sort={sort}
        pageSize={pageSize}
      />

      <div className="w-full min-w-0 overflow-hidden rounded-xl border border-black/10 bg-white shadow-sm">
        <nav
          aria-label={t("statusNav")}
          className="border-b border-black/10"
        >
          <ul className="flex items-center gap-1 overflow-x-auto px-4">
            {EVENT_LIBRARY_TABS.map((tabStatus) => {
              const isActive = tab === tabStatus;

              return (
                <li key={tabStatus} className="shrink-0">
                  <Link
                    to={buildEventLibraryHref({
                      tab: tabStatus,
                      page: 1,
                      pageSize,
                      searchQuery,
                      category,
                      dateFilters,
                      sort,
                    })}
                    className={`inline-flex border-b-2 px-3 py-3 text-sm transition-colors ${isActive
                        ? "border-whart-dark font-medium text-whart-text"
                        : "border-transparent text-neutral-600 hover:border-black/10 hover:text-whart-text"
                      }`}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {t(`statuses.${tabStatus}`)}
                    <span className="ml-1.5 text-neutral-400">
                      ({tabCounts[tabStatus]})
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex items-center justify-between gap-3 border-b border-black/10 px-4 py-3">
          <div className="inline-flex flex-wrap items-center gap-2 text-sm text-neutral-600">
            <p className="inline-flex items-center gap-2">
              {t("eventCount", { count: total })}
              {isRefreshing ? (
                <span
                  aria-label="Chargement"
                  className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
                />
              ) : null}
            </p>
            {isFrozen ? (
              <span className="inline-flex rounded-xl bg-amber-100 px-2 py-0.5 text-xs font-medium text-zinc-900">
                {tCommon("library.frozenResultsBadge")}
              </span>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {onToggleResultsFreeze ? (
              <LibraryResultsFreezeControl
                isFrozen={isFrozen}
                onToggle={onToggleResultsFreeze}
                disabled={!isFrozen && !canFreezeResults}
              />
            ) : null}
            <EventsLibraryExportButton
            tab={tab}
            category={category}
            dateFilters={dateFilters}
            searchQuery={searchQuery}
            total={total}
          />
          </div>
        </div>

        <div className="w-full min-w-0 overflow-x-auto">
          <table className="w-full min-w-[80rem] table-fixed border-collapse text-left text-sm">
            <colgroup>
              <col className="w-[17%]" />
              <col className="w-[8%]" />
              <col className="w-[9%]" />
              <col className="w-[11%]" />
              <col className="w-[7%]" />
              <col className="w-[7%]" />
              <col className="w-[9%]" />
              <col className="w-[9%]" />
              <col className="w-[9%]" />
              <col className="w-[9%]" />
              <col className="w-[8%]" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-zinc-50">
              <tr className="border-b border-black/10 text-xs font-medium uppercase tracking-wider text-neutral-500">
                <LibrarySortableHeader
                  label={t("titleColumn")}
                  column="title"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildSortHref(tab, searchQuery, category, dateFilters, sort, pageSize, "title")}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <th className="border-r border-black/5 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {tCommon("library.checklistColumn")}
                </th>
                <LibrarySortableHeader
                  label={t("categoryColumn")}
                  column="event_category"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildSortHref(tab, searchQuery, category, dateFilters, sort, pageSize, "event_category")}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("placeColumn")}
                  column="place"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildSortHref(tab, searchQuery, category, dateFilters, sort, pageSize, "place")}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("startDateColumn")}
                  column="start_date"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildSortHref(tab, searchQuery, category, dateFilters, sort, pageSize, "start_date")}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("endDateColumn")}
                  column="end_date"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildSortHref(tab, searchQuery, category, dateFilters, sort, pageSize, "end_date")}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("createdAtColumn")}
                  column="created_at"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildSortHref(tab, searchQuery, category, dateFilters, sort, pageSize, "created_at")}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("updatedAtColumn")}
                  column="updated_at"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildSortHref(tab, searchQuery, category, dateFilters, sort, pageSize, "updated_at")}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("publishedAtColumn")}
                  column="published_at"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildSortHref(tab, searchQuery, category, dateFilters, sort, pageSize, "published_at")}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={tCommon("status")}
                  column="status"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildSortHref(tab, searchQuery, category, dateFilters, sort, pageSize, "status")}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("assignmentColumn")}
                  column="assignment"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildSortHref(tab, searchQuery, category, dateFilters, sort, pageSize, "assignment")}
                  className="px-4 py-3 text-left"
                />
              </tr>
            </thead>
            <tbody>
              <EventsLibraryQuickAddRow />
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    className="px-4 py-8 text-center text-sm text-neutral-500"
                  >
                    {searchQuery ? t("emptySearch") : t("empty")}
                  </td>
                </tr>
              ) : null}
            </tbody>
            {items.map((event) => {
              const editHref = getEventSharePath(event.id);

              return (
                <tbody
                  key={event.id}
                  className="group relative cursor-pointer border-b border-black/5 transition-colors even:bg-zinc-50/50 hover:bg-amber-50/40"
                >
                  <tr>
                    <td className="relative border-r border-black/5 px-4 py-3 font-medium text-whart-text">
                      <LibraryNavigableRowLink
                        to={editHref}
                        ariaLabel={t("editEvent", { title: event.title })}
                      />
                      <span className="relative z-0 line-clamp-2 underline-offset-2 group-hover:underline">
                        {event.title}
                      </span>
                    </td>
                    <td className="border-r border-black/5 px-4 py-3">
                      <LibraryChecklistProgress
                        progress={getEventEditorialChecklistProgress(event)}
                      />
                    </td>
                    <td className="border-r border-black/5 px-4 py-3 text-neutral-600">
                      <CategoryLabel event={event} />
                    </td>
                    <td className="border-r border-black/5 px-4 py-3 text-neutral-600">
                      <PlaceCell event={event} />
                    </td>
                    <LibraryTableDateCell>
                      {formatDate(event.start_date)}
                    </LibraryTableDateCell>
                    <LibraryTableDateCell>
                      {formatDate(event.end_date)}
                    </LibraryTableDateCell>
                    <LibraryTableDateCell>
                      {formatCreatedAt(event.created_at)}
                    </LibraryTableDateCell>
                    <LibraryTableDateCell>
                      {formatUpdatedAt(event.updated_at)}
                    </LibraryTableDateCell>
                    <LibraryTableDateCell>
                      {formatPublishedAt(event.published_at)}
                    </LibraryTableDateCell>
                    <td className="border-r border-black/5 px-4 py-3">
                      <StatusBadge event={event} />
                    </td>
                    <td className="px-4 py-3">
                      <LibraryAssigneeCell assignee={event.assignee} />
                    </td>
                  </tr>
                </tbody>
              );
            })}
          </table>
        </div>

        <LibraryPagination
          page={page}
          pageSize={pageSize}
          total={total}
          buildHref={buildPaginationHref}
        />
      </div>
    </section>
  );
}
